import { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { DataTable } from "../../components/layout/DataTable";
import { MetricCard } from "../../components/layout/MetricCard";
import { Panel } from "../../components/layout/Panel";
import { WarningPanel } from "../../components/layout/WarningPanel";
import { SectionIntro } from "../../components/layout/SectionIntro";
import { apiFetch } from "../../lib/api";
import { toLocalIsoDate } from "../../lib/date";
import { formatDate, formatMoney, sumTruthStageUnits } from "../../lib/format";
import { parseNumericDraft } from "../../lib/numericDraft";
import { readNullableStringParam, readStringParam } from "../../lib/url-state";
import type {
  DashboardResponse,
  InboundOpsQueueItem,
  InboundOpsQueueResponse,
  InboundOpsSummary,
  InventoryTransfer,
  InventoryTransferListResponse,
  PurchaseOrder,
  PurchaseOrderListResponse,
  PurchaseOrderTimelineEvent,
  PurchaseOrderTimelineResponse,
  TransferOrigin,
  TransferOriginInventoryResponse,
  TransferOriginListResponse,
  TransferProposal,
  TransferProposalListResponse,
} from "../../lib/types";
import { useDomainVersion, useInvalidation } from "../../app/invalidation";

type SearchValue = string | number | boolean | null | undefined;

function applySearchParamUpdates(searchParams: URLSearchParams, updates: Record<string, SearchValue>) {
  const next = new URLSearchParams(searchParams);
  Object.entries(updates).forEach(([key, value]) => {
    if (value === null || value === undefined || value === "" || value === false) {
      next.delete(key);
      return;
    }
    next.set(key, String(value));
  });
  return next;
}

function normalizeSearchParamValue(value: SearchValue): string | null {
  if (value === null || value === undefined || value === "" || value === false) {
    return null;
  }
  return String(value);
}

function normalizeSelectionId<T extends { id: string }>(items: T[], preferredId: string | null | undefined) {
  if (preferredId && items.some((item) => item.id === preferredId)) {
    return preferredId;
  }
  return items[0]?.id || null;
}

function useOperationsWorkspace() {
  const operationsVersion = useDomainVersion("operations");
  const { markDirty } = useInvalidation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [runId, setRunId] = useState<string | null>(null);
  const [inboundOpsSummary, setInboundOpsSummary] = useState<InboundOpsSummary | null>(null);
  const [inboundOpsQueue, setInboundOpsQueue] = useState<InboundOpsQueueItem[]>([]);
  const [purchaseOrders, setPurchaseOrders] = useState<PurchaseOrder[]>([]);
  const [selectedPoId, setSelectedPoId] = useState<string | null>(null);
  const [selectedPoTimeline, setSelectedPoTimeline] = useState<PurchaseOrderTimelineEvent[]>([]);
  const [transferOrigins, setTransferOrigins] = useState<TransferOrigin[]>([]);
  const [transferProposals, setTransferProposals] = useState<TransferProposal[]>([]);
  const [inventoryTransfers, setInventoryTransfers] = useState<InventoryTransfer[]>([]);
  const [transferOriginInventory, setTransferOriginInventory] = useState<TransferOriginInventoryResponse | null>(null);
  const [selectedInboundExceptionIds, setSelectedInboundExceptionIds] = useState<string[]>([]);
  const [poIntakeShipmentId, setPoIntakeShipmentId] = useState("");
  const [poAmazonInboundPlanId, setPoAmazonInboundPlanId] = useState("");
  const [poAmazonShipmentId, setPoAmazonShipmentId] = useState("");
  const [poIntakeLineId, setPoIntakeLineId] = useState(0);
  const [poIntakeStage, setPoIntakeStage] = useState("physical_arrived");
  const [poIntakeUnits, setPoIntakeUnits] = useState("0");
  const [poIntakeShort, setPoIntakeShort] = useState("0");
  const [poIntakeDamaged, setPoIntakeDamaged] = useState("0");
  const [poIntakeLost, setPoIntakeLost] = useState("0");
  const [poIntakeDiscrepancy, setPoIntakeDiscrepancy] = useState("0");
  const [poIntakeNote, setPoIntakeNote] = useState("");
  const [transferIntakeLineId, setTransferIntakeLineId] = useState(0);
  const [transferIntakeStage, setTransferIntakeStage] = useState("amazon_received");
  const [transferIntakeUnits, setTransferIntakeUnits] = useState("0");
  const [transferIntakeShort, setTransferIntakeShort] = useState("0");
  const [transferIntakeDamaged, setTransferIntakeDamaged] = useState("0");
  const [transferIntakeLost, setTransferIntakeLost] = useState("0");
  const [transferIntakeDiscrepancy, setTransferIntakeDiscrepancy] = useState("0");
  const [transferIntakeNote, setTransferIntakeNote] = useState("");
  const parsedPoIntakeUnits = parseNumericDraft(poIntakeUnits, "integer");
  const parsedPoIntakeShort = parseNumericDraft(poIntakeShort, "integer");
  const parsedPoIntakeDamaged = parseNumericDraft(poIntakeDamaged, "integer");
  const parsedPoIntakeLost = parseNumericDraft(poIntakeLost, "integer");
  const parsedPoIntakeDiscrepancy = parseNumericDraft(poIntakeDiscrepancy, "integer");
  const poIntakeDraftsValid =
    parsedPoIntakeUnits.valid &&
    parsedPoIntakeShort.valid &&
    parsedPoIntakeDamaged.valid &&
    parsedPoIntakeLost.valid &&
    parsedPoIntakeDiscrepancy.valid;
  const poIntakeHasActionableQuantity = [
    parsedPoIntakeUnits.parsed,
    parsedPoIntakeShort.parsed,
    parsedPoIntakeDamaged.parsed,
    parsedPoIntakeLost.parsed,
    parsedPoIntakeDiscrepancy.parsed,
  ].some((value) => (value ?? 0) > 0);
  const parsedTransferIntakeUnits = parseNumericDraft(transferIntakeUnits, "integer");
  const parsedTransferIntakeShort = parseNumericDraft(transferIntakeShort, "integer");
  const parsedTransferIntakeDamaged = parseNumericDraft(transferIntakeDamaged, "integer");
  const parsedTransferIntakeLost = parseNumericDraft(transferIntakeLost, "integer");
  const parsedTransferIntakeDiscrepancy = parseNumericDraft(transferIntakeDiscrepancy, "integer");
  const transferIntakeDraftsValid =
    parsedTransferIntakeUnits.valid &&
    parsedTransferIntakeShort.valid &&
    parsedTransferIntakeDamaged.valid &&
    parsedTransferIntakeLost.valid &&
    parsedTransferIntakeDiscrepancy.valid;
  const transferIntakeHasActionableQuantity = [
    parsedTransferIntakeUnits.parsed,
    parsedTransferIntakeShort.parsed,
    parsedTransferIntakeDamaged.parsed,
    parsedTransferIntakeLost.parsed,
    parsedTransferIntakeDiscrepancy.parsed,
  ].some((value) => (value ?? 0) > 0);

  const bucket = readStringParam(searchParams, "bucket");
  const status = readStringParam(searchParams, "status");
  const sku = readStringParam(searchParams, "sku");
  const selectedTransferProposalId = readNullableStringParam(searchParams, "transferProposalId");
  const selectedTransferId = readNullableStringParam(searchParams, "transferId");
  const selectedTransferProposalIdRef = useRef(selectedTransferProposalId);
  const selectedTransferIdRef = useRef(selectedTransferId);
  const dashboardRequestSeqRef = useRef(0);
  const transferProposalsRequestSeqRef = useRef(0);
  const inventoryTransfersRequestSeqRef = useRef(0);
  const purchaseOrderTimelineRequestSeqRef = useRef(0);
  const transferOriginInventoryRequestSeqRef = useRef(0);

  useEffect(() => {
    selectedTransferProposalIdRef.current = selectedTransferProposalId;
  }, [selectedTransferProposalId]);

  useEffect(() => {
    selectedTransferIdRef.current = selectedTransferId;
  }, [selectedTransferId]);

  const filteredQueue = useMemo(() => {
    return inboundOpsQueue.filter((item) => {
      if (bucket && item.bucket !== bucket) return false;
      if (status && item.status !== status) return false;
      if (sku && item.sku !== sku) return false;
      return true;
    });
  }, [bucket, inboundOpsQueue, sku, status]);

  const selectedPo = useMemo(
    () => purchaseOrders.find((po) => po.id === selectedPoId) || null,
    [purchaseOrders, selectedPoId],
  );
  const selectedTransferProposal = useMemo(
    () => transferProposals.find((proposal) => proposal.id === selectedTransferProposalId) || null,
    [selectedTransferProposalId, transferProposals],
  );
  const selectedTransfer = useMemo(
    () => inventoryTransfers.find((transfer) => transfer.id === selectedTransferId) || null,
    [inventoryTransfers, selectedTransferId],
  );
  const selectedTransferOrigin = useMemo(() => {
    const originKey = selectedTransferProposal?.origin_key || selectedTransfer?.origin_key || null;
    if (!originKey) return null;
    return transferOrigins.find((origin) => origin.origin_key === originKey) || null;
  }, [selectedTransfer?.origin_key, selectedTransferProposal?.origin_key, transferOrigins]);
  const relatedTransferOrder = useMemo(
    () =>
      selectedTransferProposal
        ? inventoryTransfers.find((transfer) => transfer.transfer_proposal_id === selectedTransferProposal.id) || null
        : null,
    [inventoryTransfers, selectedTransferProposal],
  );

  const groupedTransferProposals = useMemo(() => {
    const groups: Record<string, TransferProposal[]> = { proposed: [], approved: [], converted: [], rejected: [] };
    for (const proposal of transferProposals) {
      if (proposal.proposal_status in groups) {
        groups[proposal.proposal_status].push(proposal);
      }
    }
    return groups;
  }, [transferProposals]);

  const groupedInventoryTransfers = useMemo(
    () => ({
      approved: inventoryTransfers.filter((transfer) => transfer.status === "approved"),
      in_transit: inventoryTransfers.filter((transfer) => transfer.status === "in_transit"),
      amazon_received: inventoryTransfers.filter((transfer) => transfer.status === "amazon_received"),
      closed: inventoryTransfers.filter((transfer) => ["sellable_available", "closed", "cancelled"].includes(transfer.status)),
    }),
    [inventoryTransfers],
  );

  function patchCurrentSearchParams(updates: Record<string, SearchValue>) {
    if ("transferProposalId" in updates) {
      selectedTransferProposalIdRef.current = normalizeSearchParamValue(updates.transferProposalId);
    }
    if ("transferId" in updates) {
      selectedTransferIdRef.current = normalizeSearchParamValue(updates.transferId);
    }
    setSearchParams((current) => applySearchParamUpdates(current, updates), { replace: true });
  }

  function updateSelection(updates: Record<string, string | null>) {
    patchCurrentSearchParams(updates);
  }

  async function loadDashboard() {
    const requestId = ++dashboardRequestSeqRef.current;
    try {
      const data = await apiFetch<DashboardResponse>("/dashboard/replenishment");
      if (requestId !== dashboardRequestSeqRef.current) {
        return;
      }
      setRunId(data.run_id);
    } catch (err) {
      if (requestId !== dashboardRequestSeqRef.current) {
        return;
      }
      throw err;
    }
  }

  async function loadInboundOps() {
    const [summaryPayload, queuePayload] = await Promise.all([
      apiFetch<InboundOpsSummary>("/inbound-ops/summary"),
      apiFetch<InboundOpsQueueResponse>("/inbound-ops/queue?overdue_only=true"),
    ]);
    setInboundOpsSummary(summaryPayload);
    setInboundOpsQueue(queuePayload.items || []);
  }

  async function loadPurchaseOrders() {
    const payload = await apiFetch<PurchaseOrderListResponse>("/purchase-orders");
    const items = payload.items || [];
    setPurchaseOrders(items);
    setSelectedPoId((current) => {
      if (current && items.some((po) => po.id === current)) return current;
      return items[0]?.id || null;
    });
  }

  async function loadPurchaseOrderTimeline(poId: string) {
    const requestId = ++purchaseOrderTimelineRequestSeqRef.current;
    setSelectedPoTimeline([]);
    try {
      const payload = await apiFetch<PurchaseOrderTimelineResponse>(`/purchase-orders/${poId}/timeline`);
      if (requestId !== purchaseOrderTimelineRequestSeqRef.current) {
        return;
      }
      setSelectedPoTimeline(payload.events || []);
    } catch (err) {
      if (requestId !== purchaseOrderTimelineRequestSeqRef.current) {
        return;
      }
      throw err;
    }
  }

  async function loadTransferOrigins() {
    const payload = await apiFetch<TransferOriginListResponse>("/transfer-origins");
    setTransferOrigins(payload.items || []);
  }

  async function loadTransferProposals(activeRunId: string) {
    const requestId = ++transferProposalsRequestSeqRef.current;
    try {
      const payload = await apiFetch<TransferProposalListResponse>(
        `/runs/recommendations/${activeRunId}/transfer-proposals`,
      );
      if (requestId !== transferProposalsRequestSeqRef.current) {
        return;
      }
      const items = payload.items || [];
      const nextId = normalizeSelectionId(items, selectedTransferProposalIdRef.current);
      setTransferProposals(items);
      patchCurrentSearchParams({ transferProposalId: nextId });
    } catch (err) {
      if (requestId !== transferProposalsRequestSeqRef.current) {
        return;
      }
      throw err;
    }
  }

  async function loadInventoryTransfers(preferredTransferId?: string | null) {
    if (preferredTransferId !== undefined) {
      selectedTransferIdRef.current = preferredTransferId;
    }

    const requestId = ++inventoryTransfersRequestSeqRef.current;
    try {
      const payload = await apiFetch<InventoryTransferListResponse>("/inventory-transfers");
      if (requestId !== inventoryTransfersRequestSeqRef.current) {
        return;
      }
      const items = payload.items || [];
      const nextId = normalizeSelectionId(items, selectedTransferIdRef.current);
      setInventoryTransfers(items);
      patchCurrentSearchParams({ transferId: nextId });
    } catch (err) {
      if (requestId !== inventoryTransfersRequestSeqRef.current) {
        return;
      }
      throw err;
    }
  }

  async function loadTransferOriginInventory(originKey: string) {
    const requestId = ++transferOriginInventoryRequestSeqRef.current;
    setTransferOriginInventory(null);
    try {
      const payload = await apiFetch<TransferOriginInventoryResponse>(
        `/transfer-origins/${encodeURIComponent(originKey)}/inventory`,
      );
      if (requestId !== transferOriginInventoryRequestSeqRef.current) {
        return;
      }
      setTransferOriginInventory(payload);
    } catch (err) {
      if (requestId !== transferOriginInventoryRequestSeqRef.current) {
        return;
      }
      throw err;
    }
  }

  async function refreshOperations() {
    await Promise.all([loadDashboard(), loadInboundOps(), loadPurchaseOrders(), loadTransferOrigins(), loadInventoryTransfers()]);
  }

  async function handleReprocessInboundTruth(payload: Record<string, unknown>) {
    setLoading(true);
    setError(null);
    try {
      await apiFetch("/inbound-ops/truth/reprocess", {
        method: "POST",
        body: JSON.stringify(payload),
      });
      await Promise.all([loadInboundOps(), loadPurchaseOrders()]);
      markDirty(["operations", "procurement", "planning", "feedback"]);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }

  async function handleShipmentTruthSave(shipmentId: string) {
    if (!selectedPo || !shipmentId) return;
    setLoading(true);
    setError(null);
    try {
      await apiFetch(`/purchase-orders/${selectedPo.id}/shipments/${shipmentId}`, {
        method: "PUT",
        body: JSON.stringify({
          amazon_inbound_plan_id: poAmazonInboundPlanId || null,
          amazon_shipment_id: poAmazonShipmentId || null,
        }),
      });
      await Promise.all([loadPurchaseOrders(), loadPurchaseOrderTimeline(selectedPo.id)]);
      markDirty(["operations", "procurement"]);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }

  async function handleTransferProposalAction(path: string) {
    if (!selectedTransferProposal) return;
    setLoading(true);
    setError(null);
    try {
      const result = await apiFetch<InventoryTransfer>(`/transfer-proposals/${selectedTransferProposal.id}/${path}`, {
        method: "POST",
        body: JSON.stringify({}),
      });
      if (path === "convert-to-transfer") {
        patchCurrentSearchParams({ transferId: result.id });
      }
      await Promise.all([
        loadInventoryTransfers(path === "convert-to-transfer" ? result.id : undefined),
        runId ? loadTransferProposals(runId) : Promise.resolve(),
        loadTransferOrigins(),
      ]);
      markDirty(["operations", "planning", "finance"]);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }

  async function handleTransferDispatch() {
    if (!selectedTransfer) return;
    setLoading(true);
    setError(null);
    try {
      await apiFetch(`/inventory-transfers/${selectedTransfer.id}/dispatch`, {
        method: "POST",
        body: JSON.stringify({ actual_ship_date: toLocalIsoDate() }),
      });
      await Promise.all([
        loadInventoryTransfers(),
        loadTransferOrigins(),
        selectedTransferOrigin ? loadTransferOriginInventory(selectedTransferOrigin.origin_key) : Promise.resolve(),
      ]);
      markDirty(["operations", "finance"]);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }

  async function handleTransferStage(stage: "amazon_received" | "sellable_available") {
    if (!selectedTransfer) return;
    setLoading(true);
    setError(null);
    try {
      await Promise.all(
        selectedTransfer.lines.map((line) =>
          apiFetch(`/inventory-transfers/${selectedTransfer.id}/record-stage`, {
            method: "POST",
            body: JSON.stringify({
              transfer_line_id: line.id,
              stage,
              event_date: toLocalIsoDate(),
              units: line.units,
              cartons: line.cartons,
              location_ref: "amazon_fc",
            }),
          }),
        ),
      );
      await Promise.all([
        loadInventoryTransfers(),
        loadTransferOrigins(),
        selectedTransferOrigin ? loadTransferOriginInventory(selectedTransferOrigin.origin_key) : Promise.resolve(),
      ]);
      markDirty(["operations", "planning"]);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }

  async function handleTransferCancel() {
    if (!selectedTransfer) return;
    setLoading(true);
    setError(null);
    try {
      await apiFetch(`/inventory-transfers/${selectedTransfer.id}/cancel`, {
        method: "POST",
        body: JSON.stringify({}),
      });
      await Promise.all([
        loadInventoryTransfers(),
        loadTransferOrigins(),
        selectedTransferOrigin ? loadTransferOriginInventory(selectedTransferOrigin.origin_key) : Promise.resolve(),
      ]);
      markDirty(["operations", "planning", "finance"]);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }

  async function handleInboundOpsBulkAction(nextStatus: string) {
    if (selectedInboundExceptionIds.length === 0) return;
    setLoading(true);
    setError(null);
    try {
      const endpoint =
        nextStatus === "resolved" || nextStatus === "written_off" || nextStatus === "dismissed"
          ? "/inbound-ops/exceptions/bulk-resolve"
          : "/inbound-ops/exceptions/bulk-update";
      await apiFetch(endpoint, {
        method: "POST",
        body: JSON.stringify({ exception_ids: selectedInboundExceptionIds, status: nextStatus }),
      });
      setSelectedInboundExceptionIds([]);
      await Promise.all([loadInboundOps(), loadPurchaseOrders(), loadInventoryTransfers(), loadTransferOrigins()]);
      markDirty(["operations", "planning", "feedback"]);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }

  async function handlePoIntakeSubmit(event: FormEvent) {
    event.preventDefault();
    if (
      !selectedPo ||
      !poIntakeShipmentId ||
      !poIntakeLineId ||
      !poIntakeDraftsValid ||
      !poIntakeHasActionableQuantity ||
      parsedPoIntakeUnits.parsed === null ||
      parsedPoIntakeShort.parsed === null ||
      parsedPoIntakeDamaged.parsed === null ||
      parsedPoIntakeLost.parsed === null ||
      parsedPoIntakeDiscrepancy.parsed === null
    ) {
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await apiFetch(`/purchase-orders/${selectedPo.id}/shipments/${poIntakeShipmentId}/record-intake`, {
        method: "POST",
        body: JSON.stringify({
          receipt_stage: poIntakeStage,
          receipt_date: toLocalIsoDate(),
          receipt_source: "manual",
          notes: poIntakeNote || undefined,
          lines: [
            {
              po_line_id: poIntakeLineId,
              units_received: parsedPoIntakeUnits.parsed,
              short_units: parsedPoIntakeShort.parsed,
              damaged_units: parsedPoIntakeDamaged.parsed,
              lost_units: parsedPoIntakeLost.parsed,
              receiving_discrepancy_units: parsedPoIntakeDiscrepancy.parsed,
              note: poIntakeNote || undefined,
            },
          ],
        }),
      });
      setPoIntakeUnits("0");
      setPoIntakeShort("0");
      setPoIntakeDamaged("0");
      setPoIntakeLost("0");
      setPoIntakeDiscrepancy("0");
      setPoIntakeNote("");
      await Promise.all([loadPurchaseOrders(), loadInboundOps(), loadTransferOrigins()]);
      markDirty(["operations", "procurement", "planning"]);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }

  async function handleTransferIntakeSubmit(event: FormEvent) {
    event.preventDefault();
    if (
      !selectedTransfer ||
      !transferIntakeLineId ||
      !transferIntakeDraftsValid ||
      !transferIntakeHasActionableQuantity ||
      parsedTransferIntakeUnits.parsed === null ||
      parsedTransferIntakeShort.parsed === null ||
      parsedTransferIntakeDamaged.parsed === null ||
      parsedTransferIntakeLost.parsed === null ||
      parsedTransferIntakeDiscrepancy.parsed === null
    ) {
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await apiFetch(`/inventory-transfers/${selectedTransfer.id}/record-intake`, {
        method: "POST",
        body: JSON.stringify({
          receipt_stage: transferIntakeStage,
          event_date: toLocalIsoDate(),
          event_source: "manual",
          location_ref: "amazon_fc",
          notes: transferIntakeNote || undefined,
          lines: [
            {
              transfer_line_id: transferIntakeLineId,
              units_received: parsedTransferIntakeUnits.parsed,
              short_units: parsedTransferIntakeShort.parsed,
              damaged_units: parsedTransferIntakeDamaged.parsed,
              lost_units: parsedTransferIntakeLost.parsed,
              receiving_discrepancy_units: parsedTransferIntakeDiscrepancy.parsed,
              note: transferIntakeNote || undefined,
            },
          ],
        }),
      });
      setTransferIntakeUnits("0");
      setTransferIntakeShort("0");
      setTransferIntakeDamaged("0");
      setTransferIntakeLost("0");
      setTransferIntakeDiscrepancy("0");
      setTransferIntakeNote("");
      await Promise.all([
        loadInventoryTransfers(),
        loadInboundOps(),
        loadTransferOrigins(),
        selectedTransferOrigin ? loadTransferOriginInventory(selectedTransferOrigin.origin_key) : Promise.resolve(),
      ]);
      markDirty(["operations", "planning"]);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    refreshOperations().catch((err) => setError((err as Error).message));
  }, [operationsVersion]);

  useEffect(() => {
    if (runId) {
      loadTransferProposals(runId).catch((err) => setError((err as Error).message));
    }
  }, [runId]);

  useEffect(() => {
    if (!selectedPoId) {
      purchaseOrderTimelineRequestSeqRef.current += 1;
      setSelectedPoTimeline([]);
      return;
    }
    loadPurchaseOrderTimeline(selectedPoId).catch((err) => setError((err as Error).message));
  }, [selectedPoId]);

  useEffect(() => {
    if (!selectedPo) return;
    setPoIntakeShipmentId(selectedPo.shipments?.[0]?.id || "");
    setPoAmazonInboundPlanId(selectedPo.shipments?.[0]?.amazon_inbound_plan_id || "");
    setPoAmazonShipmentId(selectedPo.shipments?.[0]?.amazon_shipment_id || "");
    setPoIntakeLineId(selectedPo.lines?.[0]?.id || 0);
    setPoIntakeStage(selectedPo.shipments?.[0]?.route_type?.includes("warehouse") ? "physical_arrived" : "amazon_received");
  }, [selectedPo]);

  useEffect(() => {
    if (!selectedPo || !poIntakeShipmentId) return;
    const shipment = (selectedPo.shipments || []).find((row) => row.id === poIntakeShipmentId);
    if (!shipment) return;
    setPoAmazonInboundPlanId(shipment.amazon_inbound_plan_id || "");
    setPoAmazonShipmentId(shipment.amazon_shipment_id || "");
  }, [poIntakeShipmentId, selectedPo]);

  useEffect(() => {
    if (!selectedTransfer) return;
    setTransferIntakeLineId(selectedTransfer.lines?.[0]?.id || 0);
    setTransferIntakeStage(selectedTransfer.status === "amazon_received" ? "sellable_available" : "amazon_received");
  }, [selectedTransfer]);

  useEffect(() => {
    const originKey = selectedTransferProposal?.origin_key || selectedTransfer?.origin_key || null;
    if (originKey) {
      loadTransferOriginInventory(originKey).catch((err) => setError((err as Error).message));
    } else {
      transferOriginInventoryRequestSeqRef.current += 1;
      setTransferOriginInventory(null);
    }
  }, [selectedTransfer?.origin_key, selectedTransferProposal?.origin_key]);

  return {
    loading,
    error,
    bucket,
    status,
    sku,
    filteredQueue,
    selectedTransferProposalId,
    selectedTransferId,
    inboundOpsSummary,
    selectedInboundExceptionIds,
    setSelectedInboundExceptionIds,
    updateSelection,
    setSelectedPoId,
    selectedPo,
    selectedPoTimeline,
    selectedTransferProposal,
    selectedTransfer,
    selectedTransferOrigin,
    relatedTransferOrder,
    groupedTransferProposals,
    groupedInventoryTransfers,
    transferOriginInventory,
    purchaseOrders,
    poIntakeShipmentId,
    setPoIntakeShipmentId,
    poAmazonInboundPlanId,
    setPoAmazonInboundPlanId,
    poAmazonShipmentId,
    setPoAmazonShipmentId,
    poIntakeLineId,
    setPoIntakeLineId,
    poIntakeStage,
    setPoIntakeStage,
    poIntakeUnits,
    setPoIntakeUnits,
    poIntakeShort,
    setPoIntakeShort,
    poIntakeDamaged,
    setPoIntakeDamaged,
    poIntakeLost,
    setPoIntakeLost,
    poIntakeDiscrepancy,
    setPoIntakeDiscrepancy,
    poIntakeDraftsValid,
    poIntakeHasActionableQuantity,
    poIntakeNote,
    setPoIntakeNote,
    transferIntakeLineId,
    setTransferIntakeLineId,
    transferIntakeStage,
    setTransferIntakeStage,
    transferIntakeUnits,
    setTransferIntakeUnits,
    transferIntakeShort,
    setTransferIntakeShort,
    transferIntakeDamaged,
    setTransferIntakeDamaged,
    transferIntakeLost,
    setTransferIntakeLost,
    transferIntakeDiscrepancy,
    setTransferIntakeDiscrepancy,
    transferIntakeDraftsValid,
    transferIntakeHasActionableQuantity,
    transferIntakeNote,
    setTransferIntakeNote,
    handleReprocessInboundTruth,
    handleShipmentTruthSave,
    handleTransferProposalAction,
    handleTransferDispatch,
    handleTransferStage,
    handleTransferCancel,
    handleInboundOpsBulkAction,
    handlePoIntakeSubmit,
    handleTransferIntakeSubmit,
  };
}

export function OperationsWorkspace() {
  const state = useOperationsWorkspace();
  const operationsSummary = state.inboundOpsSummary
    ? (() => {
        const summary: string[] = [];
        summary.push(`${state.inboundOpsSummary.needs_intake} records need intake and ${state.inboundOpsSummary.active_exceptions} active exceptions remain open.`);
        summary.push(`${state.inboundOpsSummary.waiting_external} items are waiting on external follow-up and ${state.inboundOpsSummary.ready_to_resolve} are ready to resolve.`);
        if ((state.inboundOpsSummary.stale_truth_streams || []).length > 0) {
          summary.push(`Stale truth streams: ${(state.inboundOpsSummary.stale_truth_streams || []).join(", ")}.`);
        } else {
          summary.push("No stale truth streams are currently flagged.");
        }
        summary.push(`${state.inboundOpsSummary.blocked_units} blocked units and ${state.inboundOpsSummary.truth_conflicts} truth conflicts need operator awareness.`);
        return summary;
      })()
    : [];

  return (
    <>
      <SectionIntro
        eyebrow="Operations"
        title="What is stuck or falling behind?"
        description="Spot receiving problems early, clear exceptions faster, and keep inbound work moving without digging through operational noise."
      />
      <Panel>
        <h2>Receiving & exceptions</h2>
        <div className="form-row">
          <label>
            Bucket
            <input value={state.bucket} onChange={(event) => state.updateSelection({ bucket: event.target.value || null })} />
          </label>
          <label>
            Status
            <input value={state.status} onChange={(event) => state.updateSelection({ status: event.target.value || null })} />
          </label>
          <label>
            SKU
            <input value={state.sku} onChange={(event) => state.updateSelection({ sku: event.target.value || null })} />
          </label>
        </div>
        <div className="detail-cards">
          <MetricCard label="Needs Intake" value={state.inboundOpsSummary?.needs_intake ?? 0} />
          <MetricCard label="Active Exceptions" value={state.inboundOpsSummary?.active_exceptions ?? 0} />
          <MetricCard label="Waiting External" value={state.inboundOpsSummary?.waiting_external ?? 0} />
          <MetricCard label="Ready To Resolve" value={state.inboundOpsSummary?.ready_to_resolve ?? 0} />
          <MetricCard label="Unmatched Truth" value={state.inboundOpsSummary?.unmatched_external_truth ?? 0} />
          <MetricCard label="Truth Conflicts" value={state.inboundOpsSummary?.truth_conflicts ?? 0} />
          <MetricCard label="Blocked Units" value={state.inboundOpsSummary?.blocked_units ?? 0} />
          <MetricCard label="Observed Units" value={sumTruthStageUnits(state.inboundOpsSummary?.observed_stage_units)} />
          <MetricCard label="Inferred Units" value={sumTruthStageUnits(state.inboundOpsSummary?.inferred_stage_units)} />
          <MetricCard
            label="Stale Truth Streams"
            value={(state.inboundOpsSummary?.stale_truth_streams || []).length > 0 ? (state.inboundOpsSummary?.stale_truth_streams || []).join(", ") : "-"}
          />
        </div>

        <div className="actions">
          <button type="button" onClick={() => state.selectedPo && state.handleReprocessInboundTruth({ po_id: state.selectedPo.id })} disabled={state.loading || !state.selectedPo}>
            Reprocess Selected PO Truth
          </button>
          <button type="button" onClick={() => state.handleInboundOpsBulkAction("investigating")} disabled={state.loading}>
            Mark Investigating
          </button>
          <button type="button" onClick={() => state.handleInboundOpsBulkAction("waiting_external")} disabled={state.loading}>
            Wait External
          </button>
          <button type="button" onClick={() => state.handleInboundOpsBulkAction("ready_to_resolve")} disabled={state.loading}>
            Ready To Resolve
          </button>
          <button type="button" onClick={() => state.handleInboundOpsBulkAction("resolved")} disabled={state.loading}>
            Resolve
          </button>
        </div>

        {operationsSummary.length > 0 && (
          <Panel>
            <h3>What needs action now</h3>
            <ul className="summary-list">
              {operationsSummary.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </Panel>
        )}

        <DataTable slim>
          <table>
            <thead>
              <tr>
                <th>Select</th>
                <th>Bucket</th>
                <th>Source</th>
                <th>Ref</th>
                <th>SKU</th>
                <th>Stage</th>
                <th>Type</th>
                <th>Status</th>
                <th>Blocked</th>
                <th>Owner</th>
                <th>Next Action</th>
                <th>Risk</th>
              </tr>
            </thead>
            <tbody>
              {state.filteredQueue.length > 0 ? (
                state.filteredQueue.slice(0, 50).map((item, index) => {
                  const selectable = Boolean(item.id);
                  const checked = selectable && state.selectedInboundExceptionIds.includes(item.id as string);
                  return (
                    <tr
                      key={`${item.bucket}-${item.id || item.source_id || index}`}
                      onClick={() => {
                        if (item.po_id) {
                          state.setSelectedPoId(item.po_id);
                        }
                        if (item.transfer_id) {
                          state.updateSelection({ transferId: item.transfer_id });
                        }
                        if (item.transfer_proposal_id) {
                          state.updateSelection({ transferProposalId: item.transfer_proposal_id });
                        }
                      }}
                    >
                      <td>
                        {selectable ? (
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={(event) =>
                              state.setSelectedInboundExceptionIds((current) =>
                                event.target.checked ? [...current, item.id as string] : current.filter((value) => value !== item.id),
                              )
                            }
                          />
                        ) : (
                          "-"
                        )}
                      </td>
                      <td>{item.bucket}</td>
                      <td>{item.source_type}</td>
                      <td>{item.shipment_ref || item.transfer_ref || item.source_id || "-"}</td>
                      <td>{item.sku || "-"}</td>
                      <td>{item.stage || "-"}</td>
                      <td>{item.exception_type || item.item_type || "-"}</td>
                      <td>{item.status || "-"}</td>
                      <td>{item.blocked_units || 0}</td>
                      <td>{item.assigned_to || item.external_party || "-"}</td>
                      <td>{item.next_action_date || item.expected_date || "-"}</td>
                      <td>{item.stockout_risk_flag ? "Stockout risk" : item.a_sku_flag ? "A-SKU" : "-"}</td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={12}>No inbound ops items.</td>
                </tr>
              )}
            </tbody>
          </table>
        </DataTable>
      </Panel>

      {state.selectedPo && (
        <Panel>
          <h2>PO Truth And Intake</h2>
          <div className="detail-cards">
            <MetricCard label="Supplier" value={state.selectedPo.supplier} />
            <MetricCard label="Status" value={state.selectedPo.status} />
            <MetricCard label="Expected Arrival" value={state.selectedPo.expected_arrival_date || "-"} />
            <MetricCard label="Observed Truth" value={sumTruthStageUnits(state.selectedPo.inbound_summary?.truth_summary?.observed_stage_units)} />
            <MetricCard label="Inferred Truth" value={sumTruthStageUnits(state.selectedPo.inbound_summary?.truth_summary?.inferred_stage_units)} />
          </div>

          {(state.selectedPo.shipments || []).length > 0 && (
            <>
              <h3>Amazon Truth Keys</h3>
              <form
                className="po-form"
                onSubmit={(event) => {
                  event.preventDefault();
                  if (!state.poIntakeShipmentId) return;
                  state.handleShipmentTruthSave(state.poIntakeShipmentId);
                }}
              >
                <label>
                  Shipment
                  <select value={state.poIntakeShipmentId} onChange={(event) => state.setPoIntakeShipmentId(event.target.value)}>
                    {(state.selectedPo.shipments || []).map((shipment) => (
                      <option key={`po-truth-shipment-${shipment.id}`} value={shipment.id}>
                        {shipment.shipment_ref} ({shipment.route_type || "route"})
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Amazon Inbound Plan
                  <input value={state.poAmazonInboundPlanId} onChange={(event) => state.setPoAmazonInboundPlanId(event.target.value)} />
                </label>
                <label>
                  Amazon Shipment Id
                  <input value={state.poAmazonShipmentId} onChange={(event) => state.setPoAmazonShipmentId(event.target.value)} />
                </label>
                <button type="submit" disabled={state.loading || !state.poIntakeShipmentId}>
                  Save Truth Keys
                </button>
                <button
                  type="button"
                  onClick={() => state.handleReprocessInboundTruth(state.poIntakeShipmentId ? { shipment_id: state.poIntakeShipmentId } : { po_id: state.selectedPo?.id })}
                  disabled={state.loading}
                >
                  Reprocess Truth
                </button>
              </form>

              <h3>Shipment Intake</h3>
              <form className="po-form" onSubmit={state.handlePoIntakeSubmit}>
                <label>
                  Shipment
                  <select value={state.poIntakeShipmentId} onChange={(event) => state.setPoIntakeShipmentId(event.target.value)}>
                    {(state.selectedPo.shipments || []).map((shipment) => (
                      <option key={`po-intake-shipment-${shipment.id}`} value={shipment.id}>
                        {shipment.shipment_ref} ({shipment.route_type || "route"})
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Line
                  <select value={state.poIntakeLineId} onChange={(event) => state.setPoIntakeLineId(Number(event.target.value))}>
                    {state.selectedPo.lines.map((line) => (
                      <option key={`po-intake-line-${line.id}`} value={line.id}>
                        {line.sku} ({line.approved_qty_units || line.units} units)
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Stage
                  <select value={state.poIntakeStage} onChange={(event) => state.setPoIntakeStage(event.target.value)}>
                    <option value="physical_arrived">physical_arrived</option>
                    <option value="amazon_received">amazon_received</option>
                    <option value="sellable_available">sellable_available</option>
                  </select>
                </label>
                <label>
                  Units Received
                  <input type="number" step="1" value={state.poIntakeUnits} onChange={(event) => state.setPoIntakeUnits(event.target.value)} />
                </label>
                <label>
                  Short
                  <input type="number" step="1" value={state.poIntakeShort} onChange={(event) => state.setPoIntakeShort(event.target.value)} />
                </label>
                <label>
                  Damaged
                  <input type="number" step="1" value={state.poIntakeDamaged} onChange={(event) => state.setPoIntakeDamaged(event.target.value)} />
                </label>
                <label>
                  Lost
                  <input type="number" step="1" value={state.poIntakeLost} onChange={(event) => state.setPoIntakeLost(event.target.value)} />
                </label>
                <label>
                  Discrepancy
                  <input
                    type="number"
                    step="1"
                    value={state.poIntakeDiscrepancy}
                    onChange={(event) => state.setPoIntakeDiscrepancy(event.target.value)}
                  />
                </label>
                <label>
                  Note
                  <input value={state.poIntakeNote} onChange={(event) => state.setPoIntakeNote(event.target.value)} />
                </label>
                <button
                  type="submit"
                  disabled={
                    state.loading ||
                    !state.poIntakeShipmentId ||
                    !state.poIntakeLineId ||
                    !state.poIntakeDraftsValid ||
                    !state.poIntakeHasActionableQuantity
                  }
                >
                  Record Partial Receipt
                </button>
              </form>
              {state.poIntakeDraftsValid && !state.poIntakeHasActionableQuantity && (
                <p className="form-helper-warning">Enter at least one received or exception quantity greater than zero.</p>
              )}
            </>
          )}

          <h3>PO Timeline</h3>
          <DataTable slim>
            <table>
              <thead>
                <tr>
                  <th>When</th>
                  <th>Kind</th>
                  <th>Details</th>
                </tr>
              </thead>
              <tbody>
                {state.selectedPoTimeline.map((event, index) => (
                  <tr key={`${event.kind}-${index}`}>
                    <td>{event.event_at || "-"}</td>
                    <td>{event.kind}</td>
                    <td>{JSON.stringify(event.payload)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </DataTable>
        </Panel>
      )}

      <Panel>
        <h2>Warehouse To Amazon Transfers</h2>
        <div className="po-workspace">
          <div className="po-columns">
            <div>
              {[
                ["Needs Approval", state.groupedTransferProposals.proposed],
                ["Approved", state.groupedTransferProposals.approved],
                ["Converted", state.groupedTransferProposals.converted],
                ["Rejected", state.groupedTransferProposals.rejected],
              ].map(([label, proposals]) => (
                <div key={label as string} className="po-status-group">
                  <h3>{label as string}</h3>
                  <DataTable slim>
                    <table>
                      <thead>
                        <tr>
                          <th>Origin</th>
                          <th>Ship</th>
                          <th>Cycle</th>
                          <th>Units</th>
                          <th>Cartons</th>
                          <th>Cost</th>
                          <th>Protected Profit</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(proposals as TransferProposal[]).map((proposal) => (
                          <tr
                            key={proposal.id}
                            className={proposal.id === state.selectedTransferProposalId ? "selected-row" : ""}
                            onClick={() => state.updateSelection({ transferProposalId: proposal.id })}
                          >
                            <td>{proposal.origin_key}</td>
                            <td>{proposal.proposal_ship_date}</td>
                            <td>{proposal.on_cycle ? "On-cycle" : "Off-cycle"}</td>
                            <td>{proposal.total_units}</td>
                            <td>{proposal.total_cartons}</td>
                            <td>{formatMoney(proposal.estimated_transfer_cost)}</td>
                            <td>{formatMoney(proposal.protected_profit_value)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </DataTable>
                </div>
              ))}

              {[
                ["Approved", state.groupedInventoryTransfers.approved],
                ["In Transit", state.groupedInventoryTransfers.in_transit],
                ["Amazon Received", state.groupedInventoryTransfers.amazon_received],
                ["Closed / Cancelled", state.groupedInventoryTransfers.closed],
              ].map(([label, transfers]) => (
                <div key={label as string} className="po-status-group">
                  <h3>{label as string}</h3>
                  <DataTable slim>
                    <table>
                      <thead>
                        <tr>
                          <th>Origin</th>
                          <th>Status</th>
                          <th>Ship</th>
                          <th>Amazon</th>
                          <th>Sellable</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(transfers as InventoryTransfer[]).map((transfer) => (
                          <tr
                            key={transfer.id}
                            className={transfer.id === state.selectedTransferId ? "selected-row" : ""}
                            onClick={() => state.updateSelection({ transferId: transfer.id })}
                          >
                            <td>{transfer.origin_key}</td>
                            <td>{transfer.status}</td>
                            <td>{transfer.actual_ship_date || transfer.planned_ship_date || "-"}</td>
                            <td>{transfer.actual_amazon_received_date || transfer.target_amazon_received_date || "-"}</td>
                            <td>{transfer.actual_sellable_available_date || transfer.target_sellable_available_date || "-"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </DataTable>
                </div>
              ))}
            </div>

            <div>
              {state.selectedTransferProposal ? (
                <>
                  <h3>
                    Transfer Proposal: {state.selectedTransferProposal.origin_key}{" "}
                    <span className="badge badge-neutral">{state.selectedTransferProposal.proposal_status}</span>
                  </h3>
                  <div className="detail-cards">
                    <MetricCard label="Ship Date" value={state.selectedTransferProposal.proposal_ship_date} />
                    <MetricCard label="Sellable ETA" value={formatDate(state.selectedTransferProposal.target_sellable_available_date || null)} />
                    <MetricCard label="Units / Cartons" value={`${state.selectedTransferProposal.total_units} / ${state.selectedTransferProposal.total_cartons}`} />
                    <MetricCard label="Transfer Cost" value={formatMoney(state.selectedTransferProposal.estimated_transfer_cost)} />
                    <MetricCard label="Protected Profit" value={formatMoney(state.selectedTransferProposal.protected_profit_value)} />
                    <MetricCard label="Cash Feasibility" value={state.selectedTransferProposal.cash_feasibility_status || "-"} />
                    <MetricCard
                      label="Origin Config"
                      value={`prep ${state.selectedTransferOrigin?.prep_days ?? "-"}d | transit ${state.selectedTransferOrigin?.transit_days ?? "-"}d`}
                    />
                    <MetricCard label="Linked Transfer" value={state.relatedTransferOrder ? state.relatedTransferOrder.id : "Not converted"} />
                  </div>

                  <div className="actions">
                    {state.selectedTransferProposal.proposal_status === "proposed" && (
                      <button type="button" onClick={() => state.handleTransferProposalAction("approve")} disabled={state.loading}>
                        Approve Transfer Proposal
                      </button>
                    )}
                    {["proposed", "approved"].includes(state.selectedTransferProposal.proposal_status) && (
                      <button type="button" onClick={() => state.handleTransferProposalAction("reject")} disabled={state.loading}>
                        Reject Transfer Proposal
                      </button>
                    )}
                    {state.selectedTransferProposal.proposal_status === "approved" && (
                      <button type="button" onClick={() => state.handleTransferProposalAction("convert-to-transfer")} disabled={state.loading}>
                        Convert To Transfer
                      </button>
                    )}
                    {state.relatedTransferOrder && (
                      <button type="button" onClick={() => state.updateSelection({ transferId: state.relatedTransferOrder?.id || null })}>
                        Open Linked Transfer
                      </button>
                    )}
                  </div>

                  <h4>Transfer Lines</h4>
                  <DataTable slim>
                    <table>
                      <thead>
                        <tr>
                          <th>SKU</th>
                          <th>Tier</th>
                          <th>Requested</th>
                          <th>Selected</th>
                          <th>Cartons</th>
                          <th>Origin Units</th>
                          <th>Reason</th>
                        </tr>
                      </thead>
                      <tbody>
                        {state.selectedTransferProposal.lines.map((line) => (
                          <tr key={`transfer-line-${line.id}`}>
                            <td>{line.sku}</td>
                            <td>{line.tier}</td>
                            <td>{line.requested_qty_units}</td>
                            <td>{line.selected_qty_units}</td>
                            <td>{line.cartons}</td>
                            <td>{line.origin_available_units}</td>
                            <td>{line.selection_reason || (line.is_supporting_fill ? "supporting_fill" : "selected")}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </DataTable>
                </>
              ) : (
                <p>No transfer proposal selected.</p>
              )}

              {state.selectedTransfer && (
                <>
                  <h3 className="subsection-title">
                    Transfer Order: {state.selectedTransfer.origin_key}{" "}
                    <span className="badge badge-neutral">{state.selectedTransfer.status}</span>
                  </h3>
                  <div className="detail-cards">
                    <MetricCard label="Proposal Link" value={state.selectedTransfer.transfer_proposal_id || "manual"} />
                    <MetricCard label="Ship Date" value={state.selectedTransfer.actual_ship_date || state.selectedTransfer.planned_ship_date || "-"} />
                    <MetricCard label="Amazon Date" value={state.selectedTransfer.actual_amazon_received_date || state.selectedTransfer.target_amazon_received_date || "-"} />
                    <MetricCard label="Sellable Date" value={state.selectedTransfer.actual_sellable_available_date || state.selectedTransfer.target_sellable_available_date || "-"} />
                    <MetricCard label="Blocked Units" value={state.selectedTransfer.blocked_units || 0} />
                  </div>

                  <div className="actions">
                    {state.selectedTransfer.status === "approved" && (
                      <button type="button" onClick={() => state.handleTransferDispatch()} disabled={state.loading}>
                        Dispatch Transfer
                      </button>
                    )}
                    {["approved", "in_transit"].includes(state.selectedTransfer.status) && (
                      <button type="button" onClick={() => state.handleTransferStage("amazon_received")} disabled={state.loading}>
                        Mark Amazon Received
                      </button>
                    )}
                    {["approved", "in_transit", "amazon_received"].includes(state.selectedTransfer.status) && (
                      <button type="button" onClick={() => state.handleTransferStage("sellable_available")} disabled={state.loading}>
                        Mark Sellable Available
                      </button>
                    )}
                    {!["closed", "cancelled"].includes(state.selectedTransfer.status) && (
                      <button type="button" onClick={() => state.handleTransferCancel()} disabled={state.loading}>
                        Cancel Transfer
                      </button>
                    )}
                  </div>

                  <h4>Transfer Intake</h4>
                  <form className="po-form" onSubmit={state.handleTransferIntakeSubmit}>
                    <label>
                      Line
                      <select value={state.transferIntakeLineId} onChange={(event) => state.setTransferIntakeLineId(Number(event.target.value))}>
                        {state.selectedTransfer.lines.map((line) => (
                          <option key={`transfer-intake-line-${line.id}`} value={line.id}>
                            {line.sku} ({line.units} units)
                          </option>
                        ))}
                      </select>
                    </label>
                    <label>
                      Stage
                      <select value={state.transferIntakeStage} onChange={(event) => state.setTransferIntakeStage(event.target.value)}>
                        <option value="amazon_received">amazon_received</option>
                        <option value="sellable_available">sellable_available</option>
                      </select>
                    </label>
                    <label>
                      Units Received
                      <input
                        type="number"
                        step="1"
                        value={state.transferIntakeUnits}
                        onChange={(event) => state.setTransferIntakeUnits(event.target.value)}
                      />
                    </label>
                    <label>
                      Short
                      <input type="number" step="1" value={state.transferIntakeShort} onChange={(event) => state.setTransferIntakeShort(event.target.value)} />
                    </label>
                    <label>
                      Damaged
                      <input
                        type="number"
                        step="1"
                        value={state.transferIntakeDamaged}
                        onChange={(event) => state.setTransferIntakeDamaged(event.target.value)}
                      />
                    </label>
                    <label>
                      Lost
                      <input type="number" step="1" value={state.transferIntakeLost} onChange={(event) => state.setTransferIntakeLost(event.target.value)} />
                    </label>
                    <label>
                      Discrepancy
                      <input
                        type="number"
                        step="1"
                        value={state.transferIntakeDiscrepancy}
                        onChange={(event) => state.setTransferIntakeDiscrepancy(event.target.value)}
                      />
                    </label>
                    <label>
                      Note
                      <input value={state.transferIntakeNote} onChange={(event) => state.setTransferIntakeNote(event.target.value)} />
                    </label>
                    <button
                      type="submit"
                      disabled={
                        state.loading ||
                        !state.transferIntakeLineId ||
                        !state.transferIntakeDraftsValid ||
                        !state.transferIntakeHasActionableQuantity
                      }
                    >
                      Record Intake
                    </button>
                  </form>
                  {state.transferIntakeDraftsValid && !state.transferIntakeHasActionableQuantity && (
                    <p className="form-helper-warning">Enter at least one received or exception quantity greater than zero.</p>
                  )}

                  <h4>Allocated Lots</h4>
                  <DataTable slim>
                    <table>
                      <thead>
                        <tr>
                          <th>SKU</th>
                          <th>Units</th>
                          <th>Cartons</th>
                          <th>Status</th>
                          <th>Allocations</th>
                        </tr>
                      </thead>
                      <tbody>
                        {state.selectedTransfer.lines.map((line) => (
                          <tr key={`transfer-order-line-${line.id}`}>
                            <td>{line.sku}</td>
                            <td>{line.units}</td>
                            <td>{line.cartons ?? "-"}</td>
                            <td>{line.line_status}</td>
                            <td>
                              {line.allocations
                                ?.map((allocation) => `${allocation.source_po_line_id}:${allocation.units_allocated}`)
                                .join(", ") || "-"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </DataTable>

                  <h4>Transfer Stage Events</h4>
                  <DataTable slim>
                    <table>
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Stage</th>
                          <th>Units</th>
                          <th>Source</th>
                          <th>Confidence</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(state.selectedTransfer.stage_events || []).map((event) => (
                          <tr key={`transfer-stage-${event.id}`}>
                            <td>{event.event_date}</td>
                            <td>{event.stage}</td>
                            <td>{event.units}</td>
                            <td>{event.event_source}</td>
                            <td>{event.confidence || "-"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </DataTable>
                </>
              )}
            </div>
          </div>
        </div>
      </Panel>

      {state.error && (
        <WarningPanel title="Receiving needs attention" warnings={[state.error]} />
      )}
    </>
  );
}
